import React from 'react';
import { get } from 'lodash';
import { CircularProgress, Dialog } from '@mui/material';
import SearchableList from '../../elements/SearchableList';
import { ReactComponent as CloseIcon } from 'new/components/icons/close-cross.svg';
import LoadableMonacoEditor from '../../elements/editors/monaco/LoadableMonacoEditor';
import { getEditorLanguage } from '../../elements/editors/monaco/MonacoEditorUtils';
import { Button } from 'new/elements/Button';
import { formatCode } from 'js/lib/FormatUtils';
import styles from './KafkaTopicsList.module.scss';

function PreviewDialogContent({
  previewedTopic: name,
  error,
  isLoadingSample,
  contentType,
  response,
  onClosePreview,
  onRefreshPreview,
}) {
  const editorLanguage = getEditorLanguage(contentType);
  const value = response || '';

  return (
    <div className={styles.preview_dialog}>
      <div>
        <div className={styles.preview_header}>
          <div>
            <h1 className={styles.preview_title}>Preview Topic</h1>
            {error ? (
              <span className={styles.preview_error_message}>(Failure: ${error})</span>
            ) : null}
          </div>
          <CloseIcon onClick={onClosePreview} />
        </div>

        <div className={styles.preview_info}>
          <div className={styles.preview_column}>
            <label className={styles.preview_label}>Topic Name: </label>
            <span className={styles.text}>{name}</span>
          </div>

          <div className={styles.preview_column}>
            <label className={styles.preview_label}>Data parser: </label>
            <span className={styles.text}>TextParser</span>
          </div>

          <div className={styles.refresh_button_container}>
            <Button
              color="info"
              variant="outlined"
              style={{ minWidth: 160, maxWidth: 160 }}
              onClick={onRefreshPreview}
            >
              {isLoadingSample ? <CircularProgress size={20} /> : 'Reload Samples'}
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.container_editor}>
        <LoadableMonacoEditor
          language={editorLanguage}
          theme="dark"
          value={value}
          options={{
            readOnly: true,
            minimap: { enabled: false },
          }}
          height="100%"
        />
      </div>
    </div>
  );
}

class KafkaTopicsList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      previewedTopic: null,
      searchableListKey: this._computeSearchableListKey(props),
    };

    this._handlePreviewClicked = this._handlePreviewClicked.bind(this);
    this._handleClosePreview = this._handleClosePreview.bind(this);
    this._handleRefreshClicked = this._handleRefreshClicked.bind(this);
  }

  componentDidMount() {
    const { selectedCredentialId, probeGetKafkaTopics } = this.props;

    probeGetKafkaTopics(selectedCredentialId);
  }

  _computeSearchableListKey(props) {
    const { sourceType, topics } = props;
    const sourceTypeKey = sourceType || 'topic';
    const topicsKey = topics && Array.isArray(topics) ? topics.join(',') : 'empty';
    return `${sourceTypeKey}-${topicsKey}`;
  }

  componentDidUpdate(prevProps) {
    const { sourceType, topics } = this.props;
    const prevSourceType = prevProps.sourceType;
    const prevTopics = prevProps.topics;

    // Only recalculate key if sourceType or topics actually changed
    if (sourceType !== prevSourceType || topics !== prevTopics) {
      this.setState({
        searchableListKey: this._computeSearchableListKey(this.props),
      });
    }
  }

  render() {
    const {
      topics,
      samples,
      selectedTopic,
      sourceType,
      isLoadingTopics,
      isLoadingSample,
      isPreviewEnabled,
      onTopicChanged,
      hideCreateBtn,
    } = this.props;
    const { previewedTopic } = this.state;
    const topicSamples = get(samples, previewedTopic);

    let error;
    let response;
    let contentType;

    if (topicSamples) {
      error = topicSamples.error;
      contentType = topicSamples.contentType;
      response = formatCode(topicSamples.response, contentType);
    } else {
      error = null;
      response = '';
    }

    return (
      <div>
        <SearchableList
          key={this.state.searchableListKey}
          data={topics}
          selectedItem={selectedTopic}
          isLoading={isLoadingTopics}
          isPreviewEnabled={isPreviewEnabled}
          onPreviewClicked={this._handlePreviewClicked}
          onCreate={onTopicChanged}
          onChange={onTopicChanged}
          hideCreateBtn={hideCreateBtn || false}
        />

        <Dialog
          fullWidth
          maxWidth="md"
          open={!!previewedTopic}
          onClose={this._handleClosePreview}
          className={styles.preview_dialog}
        >
          <PreviewDialogContent
            previewedTopic={previewedTopic}
            error={error}
            isLoadingSample={isLoadingSample}
            contentType={contentType}
            response={response}
            onClosePreview={this._handleClosePreview}
            onRefreshPreview={this._handleRefreshClicked}
          />
        </Dialog>
      </div>
    );
  }

  _handlePreviewClicked(topic) {
    const { selectedCredentialId, probeGetKafkaTopicSample, topicFieldName } = this.props;
    this.setState({ previewedTopic: topic });
    probeGetKafkaTopicSample(selectedCredentialId, topic, topicFieldName);
  }

  _handleClosePreview() {
    this.setState({ previewedTopic: null });
  }

  _handleRefreshClicked() {
    const { selectedCredentialId, probeGetKafkaTopicSample, topicFieldName } = this.props;
    const { previewedTopic } = this.state;
    probeGetKafkaTopicSample(selectedCredentialId, previewedTopic, topicFieldName);
  }
}

export default KafkaTopicsList;
